import { LEVEL_WIDTH, TileData, TileType } from "./types";
import { tileKey, type EnemySpawn, type LevelData } from "./level";

// World 2-4: Water level (rendered as overworld with blue-tinted blocks)
// Long stretches of water (empty space replaced by solid floor), platforms, many enemies
export function buildLevel2_4(): LevelData {
  const tiles = new Map<string, TileData>();
  const groundTop = -1;
  const groundDepth = 2;

  // water level layout: mostly solid with some gaps and platforms
  const pits: Array<[number, number]> = [
    [13, 14],
    [30, 31],
    [48, 49],
    [66, 67],
    [84, 85],
    [102, 103],
    [120, 121],
    [138, 139]
  ];

  const setTile = (x: number, y: number, type: TileType, payload?: TileData["payload"], flower?: boolean): void => {
    tiles.set(tileKey(x, y), { type, payload, broken: false, bump: 0, flower: flower || undefined });
  };

  const isPit = (x: number): boolean => pits.some(([a, b]) => x >= a && x <= b);

  for (let x = 0; x < LEVEL_WIDTH; x++) {
    if (isPit(x)) continue;
    for (let dy = 0; dy < groundDepth; dy++) {
      setTile(x, groundTop - dy, TileType.Ground);
    }
  }

  const stair = (x: number, h: number): void => {
    for (let dy = 0; dy < h; dy++) setTile(x, groundTop + 1 + dy, TileType.Block);
  };

  // early platforms over water
  for (let x = 15; x <= 18; x++) setTile(x, 4, TileType.Brick);
  setTile(16, 4, TileType.Question, { kind: "coin" });
  setTile(17, 4, TileType.Question, { kind: "mushroom" });

  // pipes on elevated ground
  const pipes: Array<[number, number]> = [
    [22, 2],
    [35, 3],
    [52, 2],
    [69, 3],
    [88, 2],
    [106, 3],
    [124, 2],
    [142, 3],
    [160, 4],
    [172, 2],
    [180, 3]
  ];
  for (const [px, h] of pipes) {
    for (let dy = 0; dy < h; dy++) {
      setTile(px, groundTop + 1 + dy, TileType.Pipe);
      setTile(px + 1, groundTop + 1 + dy, TileType.Pipe);
    }
  }

  // floating blocks
  setTile(26, 4, TileType.Brick);
  setTile(27, 4, TileType.Brick);
  setTile(28, 4, TileType.Question, { kind: "coin" });
  setTile(29, 4, TileType.Brick);

  setTile(42, 4, TileType.Brick);
  setTile(43, 4, TileType.Brick);
  setTile(44, 4, TileType.Brick);
  setTile(45, 4, TileType.Question, { kind: "mushroom" });

  setTile(58, 4, TileType.Brick);
  setTile(59, 4, TileType.Brick);
  setTile(60, 4, TileType.Question, { kind: "coin" });

  setTile(74, 4, TileType.Brick);
  setTile(75, 4, TileType.Brick);
  setTile(76, 4, TileType.Brick);
  setTile(77, 4, TileType.Question, { kind: "coin" });

  setTile(92, 4, TileType.Brick);
  setTile(93, 4, TileType.Brick);
  setTile(94, 4, TileType.Question, { kind: "coin" });

  setTile(110, 4, TileType.Brick);
  setTile(111, 4, TileType.Brick);
  setTile(112, 4, TileType.Brick);
  setTile(113, 4, TileType.Brick);
  setTile(114, 4, TileType.Question, { kind: "mushroom" });

  setTile(128, 4, TileType.Brick);
  setTile(129, 4, TileType.Brick);
  setTile(130, 4, TileType.Brick);
  setTile(131, 4, TileType.Question, { kind: "coin" });

  setTile(146, 4, TileType.Brick);
  setTile(147, 4, TileType.Brick);
  setTile(148, 4, TileType.Brick);

  setTile(164, 4, TileType.Brick);
  setTile(165, 4, TileType.Brick);
  setTile(166, 4, TileType.Question, { kind: "coin" });
  setTile(167, 4, TileType.Brick);

  // high coins
  setTile(33, 7, TileType.Question, { kind: "coin" });
  setTile(50, 7, TileType.Question, { kind: "coin" });
  setTile(67, 7, TileType.Question, { kind: "coin" });
  setTile(85, 7, TileType.Question, { kind: "coin" });
  setTile(103, 7, TileType.Question, { kind: "coin" });
  setTile(121, 7, TileType.Question, { kind: "coin" });
  setTile(139, 7, TileType.Question, { kind: "coin" });

  // final staircase up
  stair(181, 1);
  stair(182, 2);
  stair(183, 3);
  stair(184, 4);
  stair(185, 4);
  stair(186, 4);
  stair(187, 4);
  stair(188, 3);
  stair(189, 2);
  stair(190, 1);

  const flagX = 198;
  for (let y = groundTop + 1; y <= 21; y++) setTile(flagX, y, TileType.Pole);
  setTile(flagX, 22, TileType.Block);

  const castleX = 202;

  const enemies: EnemySpawn[] = [
    { kind: "goomba", x: 16 },
    { kind: "koopa", x: 24 },
    { kind: "goomba", x: 30 },
    { kind: "goomba", x: 36 },
    { kind: "goomba", x: 42 },
    { kind: "koopa", x: 48 },
    { kind: "goomba", x: 54 },
    { kind: "goomba", x: 60 },
    { kind: "goomba", x: 66 },
    { kind: "koopa", x: 72 },
    { kind: "goomba", x: 78 },
    { kind: "goomba", x: 84 },
    { kind: "goomba", x: 90 },
    { kind: "koopa", x: 96 },
    { kind: "goomba", x: 102 },
    { kind: "goomba", x: 108 },
    { kind: "goomba", x: 114 },
    { kind: "koopa", x: 120 },
    { kind: "goomba", x: 126 },
    { kind: "goomba", x: 132 },
    { kind: "goomba", x: 138 },
    { kind: "koopa", x: 144 },
    { kind: "goomba", x: 150 },
    { kind: "goomba", x: 156 },
    { kind: "goomba", x: 162 },
    { kind: "koopa", x: 168 },
    { kind: "goomba", x: 174 },
    { kind: "goomba", x: 180 },
    { kind: "goomba", x: 186 }
  ];

  return { tiles, enemies, enterablePipes: [], flagX, castleX, width: LEVEL_WIDTH, groundY: 0 };
}
